import { Monitor, Tablet, Smartphone } from 'lucide-react';

/* ─── Laptop Frame ─── */
export function LaptopFrame({ children, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="bg-[#1F2937] rounded-t-xl pt-2 px-2">
        <div className="flex items-center gap-1.5 px-3 py-1.5">
          <div className="w-2 h-2 rounded-full bg-[#EF4444]/70" />
          <div className="w-2 h-2 rounded-full bg-[#F59E0B]/70" />
          <div className="w-2 h-2 rounded-full bg-[#22C55E]/70" />
          <div className="flex-1 mx-8">
            <div className="bg-[#374151] rounded-md h-4 flex items-center px-2">
              <span className="text-[8px] text-[#9CA3AF] font-mono truncate">app.mushinlabs.com</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-t-lg overflow-hidden">{children}</div>
      </div>
      <div className="bg-[#374151] h-3 rounded-b-lg mx-8" />
      <div className="bg-[#4B5563] h-1.5 rounded-b-md mx-20" />
    </div>
  );
}

/* ─── Tablet Frame ─── */
export function TabletFrame({ children, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="bg-[#1F2937] rounded-2xl p-2">
        <div className="flex justify-center py-1">
          <div className="w-12 h-1 bg-[#374151] rounded-full" />
        </div>
        <div className="bg-white rounded-xl overflow-hidden">{children}</div>
        <div className="flex justify-center py-1.5">
          <div className="w-6 h-6 rounded-full border-2 border-[#374151]" />
        </div>
      </div>
    </div>
  );
}

/* ─── Phone Frame ─── */
export function PhoneFrame({ children, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="bg-[#1F2937] rounded-[24px] p-1.5">
        <div className="flex justify-center py-1">
          <div className="w-16 h-1 bg-[#374151] rounded-full" />
        </div>
        <div className="bg-white rounded-[18px] overflow-hidden">{children}</div>
        <div className="h-1.5" />
      </div>
    </div>
  );
}

/* ─── Mini Dashboard: Quality KPIs ─── */
export function QualityDashboard() {
  return (
    <div className="p-3 text-[10px]">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
        <span className="font-bold text-[#111827] text-[11px]">Quality Dashboard</span>
        <span className="ml-auto text-[8px] text-[#9CA3AF] font-mono">Live</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5 mb-2">
        {[
          { l: 'FPY', v: '97.3%', c: '#059669' },
          { l: 'PPM', v: '120', c: '#C41E3A' },
          { l: 'CAPA', v: '4 Open', c: '#EA580C' },
          { l: 'Audit', v: 'Ready', c: '#059669' },
        ].map((m, i) => (
          <div key={i} className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
            <div className="text-[7px] text-[#9CA3AF] uppercase">{m.l}</div>
            <div className="font-bold text-[11px]" style={{ color: m.c }}>{m.v}</div>
          </div>
        ))}
      </div>
      <div className="bg-[#F9FAFB] rounded p-2 border border-[#F3F4F6]">
        <div className="text-[7px] text-[#9CA3AF] uppercase mb-1">Compliance Trend</div>
        <svg viewBox="0 0 200 30" className="w-full h-6">
          <polyline points="0,25 30,22 60,18 90,15 120,16 150,10 180,8 200,5" fill="none" stroke="#C41E3A" strokeWidth="1.5" />
          <polyline points="0,28 30,26 60,24 90,22 120,20 150,18 180,15 200,12" fill="none" stroke="#D1D5DB" strokeWidth="1" strokeDasharray="3,3" />
        </svg>
      </div>
    </div>
  );
}

/* ─── Mini Dashboard: IoT / OEE ─── */
export function IoTDashboard() {
  return (
    <div className="p-3 text-[10px]">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[#059669] animate-pulse" />
        <span className="font-bold text-[#111827] text-[11px]">IoT & Machine Data</span>
        <span className="ml-auto text-[8px] text-[#9CA3AF] font-mono">Real-time</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {[
          { l: 'OEE', v: '84.2%', c: '#059669' },
          { l: 'Availability', v: '92.1%', c: '#0284C7' },
          { l: 'Performance', v: '91.4%', c: '#7C3AED' },
        ].map((m, i) => (
          <div key={i} className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
            <div className="text-[7px] text-[#9CA3AF] uppercase">{m.l}</div>
            <div className="font-bold text-[11px]" style={{ color: m.c }}>{m.v}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-1 mb-2">
        {['CNC-01', 'CNC-02', 'VMC-03', 'Press-01', 'Press-02', 'Lathe-01', 'Drill-04', 'Grind-02'].map((m, i) => (
          <div key={i} className={`text-center rounded py-1 text-[7px] font-medium ${i % 3 === 1 ? 'bg-[#FEF2F2] text-[#C41E3A]' : 'bg-[#ECFDF5] text-[#059669]'}`}>
            {m}
          </div>
        ))}
      </div>
      <div className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
        <div className="text-[7px] text-[#9CA3AF] uppercase mb-1">Output vs Target</div>
        <div className="flex gap-0.5">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="flex-1 flex flex-col gap-0.5 items-center">
              <div className="w-full rounded-sm bg-[#C41E3A]/20" style={{ height: `${8 + Math.random() * 14}px` }} />
              <div className="w-full rounded-sm bg-[#C41E3A]" style={{ height: `${6 + Math.random() * 12}px` }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Mini Dashboard: Vendor Connect ─── */
export function VendorDashboard() {
  return (
    <div className="p-3 text-[10px]">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
        <span className="font-bold text-[#111827] text-[11px]">Vendor Connect</span>
        <span className="ml-auto text-[8px] text-[#9CA3AF] font-mono">Dashboard</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {[
          { l: 'Active', v: '48', c: '#059669' },
          { l: 'Under Review', v: '7', c: '#EA580C' },
          { l: 'Blocked', v: '2', c: '#C41E3A' },
        ].map((m, i) => (
          <div key={i} className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
            <div className="text-[7px] text-[#9CA3AF] uppercase">{m.l}</div>
            <div className="font-bold text-[11px]" style={{ color: m.c }}>{m.v}</div>
          </div>
        ))}
      </div>
      {[
        { name: 'Supplier A', score: 96, status: 'Preferred', color: '#059669' },
        { name: 'Supplier B', score: 84, status: 'Approved', color: '#0284C7' },
        { name: 'Supplier C', score: 66, status: 'Review', color: '#EA580C' },
      ].map((s, i) => (
        <div key={i} className="flex items-center gap-2 py-1 border-b border-[#F3F4F6] last:border-0">
          <span className="text-[9px] text-[#374151] font-medium flex-1">{s.name}</span>
          <div className="w-12 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${s.score}%`, backgroundColor: s.color }} />
          </div>
          <span className="text-[7px] font-medium px-1 py-0.5 rounded" style={{ color: s.color, backgroundColor: `${s.color}10` }}>{s.status}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Mini: Mobile Checksheet ─── */
export function MobileChecksheet() {
  return (
    <div className="p-2.5 text-[9px]">
      <div className="flex items-center justify-between mb-2">
        <span className="font-bold text-[#111827] text-[10px]">Incoming QC</span>
        <span className="text-[7px] px-1.5 py-0.5 bg-[#FEF2F2] text-[#C41E3A] rounded font-medium">In Progress</span>
      </div>
      {['Visual check: OK', 'Dimension 1: 12.05mm', 'Dimension 2: 8.22mm', 'Surface finish: OK', 'Hardness: 62 HRC'].map((item, i) => (
        <div key={i} className="flex items-center gap-1.5 py-1.5 border-b border-[#F3F4F6] last:border-0">
          <div className={`w-3.5 h-3.5 rounded flex items-center justify-center ${i < 3 ? 'bg-[#ECFDF5]' : 'bg-[#F3F4F6]'}`}>
            {i < 3 ? <span className="text-[7px] text-[#059669] font-bold">✓</span> : <span className="text-[7px] text-[#9CA3AF]">○</span>}
          </div>
          <span className="text-[#374151]">{item}</span>
        </div>
      ))}
      <button className="w-full mt-2 bg-[#C41E3A] text-white rounded-lg py-1.5 text-[8px] font-bold">Submit Inspection</button>
    </div>
  );
}

/* ─── Mini: Training Dashboard ─── */
export function TrainingDashboard() {
  return (
    <div className="p-3 text-[10px]">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
        <span className="font-bold text-[#111827] text-[11px]">Training / DOJO</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {[
          { l: 'Trained', v: '89%', c: '#059669' },
          { l: 'Pending', v: '14', c: '#EA580C' },
          { l: 'VR Sessions', v: '126', c: '#7C3AED' },
        ].map((m, i) => (
          <div key={i} className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
            <div className="text-[7px] text-[#9CA3AF] uppercase">{m.l}</div>
            <div className="font-bold text-[11px]" style={{ color: m.c }}>{m.v}</div>
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {[
          { name: 'Fire Safety', p: 95, c: '#059669' },
          { name: 'Welding Cert', p: 78, c: '#EA580C' },
          { name: 'CNC Operation', p: 100, c: '#059669' },
          { name: 'Quality Insp.', p: 62, c: '#C41E3A' },
        ].map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[8px] text-[#374151] w-16 truncate">{t.name}</span>
            <div className="flex-1 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${t.p}%`, backgroundColor: t.c }} />
            </div>
            <span className="text-[8px] font-medium" style={{ color: t.c }}>{t.p}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Mini: Audit Scorecard ─── */
export function AuditScorecard() {
  return (
    <div className="p-3 text-[10px]">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
        <span className="font-bold text-[#111827] text-[11px]">Audit Scorecard</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5 mb-2">
        {[
          { l: 'Internal Audit', v: '92%', c: '#059669' },
          { l: 'Process Audit', v: '88%', c: '#0284C7' },
          { l: 'Product Audit', v: '95%', c: '#059669' },
          { l: 'Dock Audit', v: '100%', c: '#059669' },
        ].map((m, i) => (
          <div key={i} className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
            <div className="text-[7px] text-[#9CA3AF] uppercase">{m.l}</div>
            <div className="font-bold text-[11px]" style={{ color: m.c }}>{m.v}</div>
          </div>
        ))}
      </div>
      <div className="bg-[#F9FAFB] rounded p-2 border border-[#F3F4F6]">
        <div className="text-[7px] text-[#9CA3AF] uppercase mb-1">Finding Status</div>
        <div className="flex gap-1">
          {[40, 25, 20, 15].map((w, i) => (
            <div key={i} className="h-2 rounded-full" style={{ width: `${w}%`, backgroundColor: ['#059669', '#0284C7', '#EA580C', '#C41E3A'][i] }} />
          ))}
        </div>
        <div className="flex justify-between mt-1 text-[7px] text-[#6B7280]">
          <span>Closed</span><span>In Progress</span><span>Overdue</span><span>New</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Mini: Traceability Screen ─── */
export function TraceabilityScreen() {
  return (
    <div className="p-3 text-[10px]">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[#0284C7]" />
        <span className="font-bold text-[#111827] text-[11px]">Traceability</span>
      </div>
      <div className="bg-[#F9FAFB] rounded p-2 border border-[#F3F4F6] mb-2">
        <div className="text-[7px] text-[#9CA3AF] uppercase mb-1">Part Genealogy</div>
        <div className="flex items-center gap-1 text-[8px]">
          <span className="px-1.5 py-0.5 bg-[#111827] text-white rounded text-[7px] font-mono">RM-4521</span>
          <span className="text-[#C41E3A]">→</span>
          <span className="px-1.5 py-0.5 bg-[#FEF2F2] text-[#C41E3A] rounded text-[7px] font-mono">WIP-8832</span>
          <span className="text-[#C41E3A]">→</span>
          <span className="px-1.5 py-0.5 bg-[#ECFDF5] text-[#059669] rounded text-[7px] font-mono">FG-1199</span>
        </div>
      </div>
      <div className="space-y-1">
        {[
          { l: 'Batch', v: 'B-2024-0891' },
          { l: 'Machine', v: 'CNC-03' },
          { l: 'Operator', v: 'SK Sharma' },
          { l: 'QC Status', v: 'Accepted' },
        ].map((r, i) => (
          <div key={i} className="flex justify-between py-0.5 border-b border-[#F3F4F6] last:border-0">
            <span className="text-[#6B7280]">{r.l}</span>
            <span className="font-medium text-[#111827]">{r.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
